import { IconButton, Theme, createStyles, makeStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { BasePage, colors, dimensions } from 'design-system';
import React, { useEffect, useMemo } from 'react';
import { XCircle } from 'react-feather';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  COMPLETE_VEHICLE,
  THIRD_PARTY_PACK,
} from 'data-layer-cr/dist/constants/products';
import { mustShowAllUnits } from 'data-layer-cr/dist/functions/quotations';
import {
  GeneralCatalogItem,
  ProductCoverageCatalog,
  Quotation,
} from 'data-layer-cr/dist/models';

// Models
import ModelsCarItem from 'src/models/Catalogs/ModelsCarItem';

// Redux related
import { requestCoveragesForProduct } from 'src/store/coverage/actions';
import { resetCreationFormValues } from 'src/store/quotation/actions';
import { RootState } from 'src/store/rootReducer';

// Functions
import { hasUnits } from 'src/functions/quotationMethods';

// layout
import Layout from 'src/layout';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';
import { displayHeaderContent } from './functions/displayContent';

// Components
import { QuotationButtons } from './components';
import {
  IndividualDetails,
  MultipleOptionVehicle,
  MultipleOptionVehicleRC,
  RegularDetails,
} from './components/DetailsContent';

// Hooks
import { useCoverageColectiveResume } from './hooks';

export const CLOSE_ICON_SIZE = 24;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeIcon: {
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        right: dimensions.desktopSidePadding + CLOSE_ICON_SIZE,
        top: '50%',
        marginTop: -CLOSE_ICON_SIZE / 2.0,
      },
    },
  }),
);

export const QuotationDetailsScreen = ({
  currentQuotation,
  coveragesForProduct,
  differentDriver,
  requestCoveragesForProductAction,
  resetCreationFormValuesAction,
  currentProfile,
}: PropsFromRedux): JSX.Element => {
  const classes = useStyles();
  const productType = currentQuotation.productId;

  /** Header Title */
  const headerTitle: React.ReactNode = displayHeaderContent();

  const { coverages, coverageResumes, additionalBenefits } =
    useCoverageColectiveResume({
      coveragesForProduct,
      productType,
    });

  const detailsContent = useMemo(() => {
    if (mustShowAllUnits(currentQuotation)) {
      if (currentQuotation.productId === COMPLETE_VEHICLE) {
        return (
          <MultipleOptionVehicle
            data-testid="multiple-complete-vehicle"
            quotation={currentQuotation}
            coverageCatalogs={coverages}
            coverageCatalogResumes={coverageResumes}
            differentDriver={differentDriver}
          />
        );
      }
      if (currentQuotation.productId === THIRD_PARTY_PACK) {
        return (
          <MultipleOptionVehicleRC
            data-testid="multiple-vehicle-rc-pack"
            quotation={currentQuotation}
            coverageCatalogs={coverages}
            coverageCatalogResumes={coverageResumes}
            differentDriver={differentDriver}
            currentProfile={currentProfile}
          />
        );
      }
    }
    if (currentProfile.type === 'colectiva') {
      return (
        <RegularDetails
          data-testid="regular-details"
          quotation={currentQuotation}
          coverageCatalogs={coverages}
          coverageCatalogResumes={coverageResumes}
          differentDriver={differentDriver}
          currentProfile={currentProfile}
          additionalBenefits={additionalBenefits}
        />
      );
    }

    return (
      <IndividualDetails
        data-testid="individual-details"
        quotation={currentQuotation}
        coverageCatalogs={coverages}
        coverageCatalogResumes={coverageResumes}
        currentProfile={currentProfile}
      />
    );
  }, [
    currentQuotation,
    coverages,
    coverageResumes,
    differentDriver,
    currentProfile,
    additionalBenefits,
  ]);

  const coverageAmount = coverages.length;
  useEffect(() => {
    if (productType !== '' && coverageAmount <= 0) {
      requestCoveragesForProductAction(
        productType,
        currentQuotation.units[0].unit.use,
      );
    }
  }, [
    requestCoveragesForProductAction,
    productType,
    coverageAmount,
    currentQuotation,
  ]);

  const history = useNavigate();

  // Check if there is a valid quotation present
  if (!hasUnits(currentQuotation)) {
    history('/');
  }

  function navigateToDashboard(): void {
    resetCreationFormValuesAction();
    history('/');
  }

  return (
    <Layout productId={currentQuotation.productId}>
      <BasePage
        /** We have to check if a planType exists,
         * otherwise just display the prices */
        title={headerTitle}
        appBarPosition="sticky"
        rightActions={
          <IconButton
            id="sales-web-button-close"
            data-testid="close-button"
            className={classes.closeIcon}
            onClick={navigateToDashboard}
          >
            <XCircle size={CLOSE_ICON_SIZE} color={colors.white} />
          </IconButton>
        }
      >
        <Grid container>
          <Grid item xs={12}>
            {detailsContent}
          </Grid>
          <Grid item xs={12}>
            <QuotationButtons quotation={currentQuotation} />
          </Grid>
        </Grid>
      </BasePage>
    </Layout>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  currentQuotation: Quotation;
  brands: GeneralCatalogItem[];
  models: ModelsCarItem[];
  coveragesForProduct: ProductCoverageCatalog;
  differentDriver: boolean;
  currentProfile: PricingProfileResponse;
} => ({
  currentQuotation: state.quotation.currentQuotation,
  brands: state.catalog.brands,
  models: state.catalog.models,
  coveragesForProduct: state.coverage.coveragesForProduct,
  differentDriver: state.customer.currentCustomer.driverAndCustomerAreDifferent,
  currentProfile: state.quotation.currentProfile,
});

const reduxAction = {
  requestCoveragesForProductAction: requestCoveragesForProduct,
  resetCreationFormValuesAction: resetCreationFormValues,
};
const connector = connect(mapStateToProps, reduxAction);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(QuotationDetailsScreen);
